/**
 * Text Component for Puck Editor
 * 
 * Rich text block dengan formatting options
 */

import type { ComponentConfig } from '@measured/puck'

export interface TextProps {
  content: string
  size: 'small' | 'medium' | 'large'
  weight: 'normal' | 'medium' | 'semibold' | 'bold'
  alignment: 'left' | 'center' | 'right' | 'justify'
  color?: string
  maxWidth: 'none' | 'prose' | 'narrow'
}

const sizeClasses = {
  small: 'text-sm',
  medium: 'text-base',
  large: 'text-lg',
}

const weightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
}

const alignmentClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
}

const maxWidthClasses = {
  none: '',
  prose: 'max-w-prose',
  narrow: 'max-w-xl',
}

export function Text({
  content,
  size,
  weight,
  alignment,
  color,
  maxWidth,
}: TextProps) {
  // Validate color format - supports hex (3,4,6,8 digits), rgb/rgba, hsl/hsla
  const isValidColor = (c?: string): boolean => {
    if (!c) return false
    // Support 3, 4, 6, and 8 digit hex colors
    const hexPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/
    // Basic validation for rgb/rgba - checks structure
    const rgbPattern = /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/
    // Basic validation for hsl/hsla - checks structure
    const hslPattern = /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*(,\s*[\d.]+\s*)?\)$/
    return hexPattern.test(c) || rgbPattern.test(c) || hslPattern.test(c)
  }

  // Simple markdown-like parsing for basic formatting
  const parseContent = (text: string) => {
    // Split by newlines and create paragraphs
    return text.split('\n\n').map((paragraph, index) => {
      return (
        <p key={`p-${index}`} className="mb-4 last:mb-0">
          {paragraph.split('\n').map((line, lineIndex) => {
            return (
              <span key={`l-${index}-${lineIndex}`}>
                {lineIndex > 0 && <br />}
                {line}
              </span>
            )
          })}
        </p>
      )
    })
  }

  return (
    <div
      className={`${sizeClasses[size]} ${weightClasses[weight]} ${alignmentClasses[alignment]} ${maxWidthClasses[maxWidth]} ${maxWidth !== 'none' ? 'mx-auto' : ''}`}
      style={{ color: isValidColor(color) ? color : undefined }}
    >
      {parseContent(content)}
    </div>
  )
}

export const TextConfig: ComponentConfig<TextProps> = {
  label: 'Teks',
  defaultProps: {
    content: 'Tulis konten Anda di sini...',
    size: 'medium',
    weight: 'normal',
    alignment: 'left',
    color: '',
    maxWidth: 'none',
  },
  fields: {
    content: {
      type: 'textarea',
      label: 'Konten',
    },
    size: {
      type: 'select',
      label: 'Ukuran',
      options: [
        { label: 'Kecil', value: 'small' },
        { label: 'Normal', value: 'medium' },
        { label: 'Besar', value: 'large' },
      ],
    },
    weight: {
      type: 'select',
      label: 'Ketebalan',
      options: [
        { label: 'Normal', value: 'normal' },
        { label: 'Medium', value: 'medium' },
        { label: 'Semi Bold', value: 'semibold' },
        { label: 'Bold', value: 'bold' },
      ],
    },
    alignment: {
      type: 'radio',
      label: 'Posisi',
      options: [
        { label: 'Kiri', value: 'left' },
        { label: 'Tengah', value: 'center' },
        { label: 'Kanan', value: 'right' },
        { label: 'Rata', value: 'justify' },
      ],
    },
    color: {
      type: 'text',
      label: 'Warna (HEX)',
    },
    maxWidth: {
      type: 'select',
      label: 'Lebar Maksimum',
      options: [
        { label: 'Penuh', value: 'none' },
        { label: 'Prose', value: 'prose' },
        { label: 'Sempit', value: 'narrow' },
      ],
    },
  },
  render: Text,
}
